import {
  ArrowLeft,
  Home,
  Calendar,
  Users,
  MessageCircle,
  User,
} from 'lucide-react';
import { useApp } from './AppContext';
import { useTranslation } from '../i18n/TranslationContext';

interface MainLayoutProps {
  children: React.ReactNode;
  showBack?: boolean;
  onBack?: () => void;
  showBottomNav?: boolean;
  showAddButton?: boolean;
  onAddClick?: () => void;
}

export function MainLayout({
  children,
  showBack = false,
  onBack,
  showBottomNav = true,
  showAddButton = false,
  onAddClick,
}: MainLayoutProps) {
  const {
    currentScreen,
    setCurrentScreen,
    navigateWithProcessCheck,
    canAccessScreen,
    getCurrentStep,
  } = useApp();
  const { t } = useTranslation();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Default back behavior: return to dashboard (home/summary screen)
      setCurrentScreen('dashboard');
    }
  };

  // Updated bottom navigation structure for UCM Airport Inspector
  const navItems = [
    { id: 'dashboard', label: t('navigation.dashboard'), icon: Home, screen: 'dashboard' },
    {
      id: 'events',
      label: t('navigation.events'),
      icon: Calendar,
      screen: 'app-event-application',
    },
    { id: 'group', label: t('navigation.group'), icon: Users, screen: 'app-group' },
    { id: 'chat', label: t('navigation.chat'), icon: MessageCircle, screen: 'app-chat' },
    { id: 'profile', label: t('navigation.profile'), icon: User, screen: 'app-settings' },
  ];

  // Function to determine active state based on screen context
  const getActiveTab = () => {
    const screen = currentScreen;

    // Dashboard tab active on: dashboard and related screens
    if (['dashboard', 'task-list'].includes(screen)) {
      return 'dashboard';
    }

    // Events tab active on: event application and event details
    if (
      [
        'app-event-application',
        'app-event-details-1',
        'app-event-details-2',
        'apply',
      ].includes(screen)
    ) {
      return 'events';
    }

    // Group tab active on: group management
    if (['app-group'].includes(screen)) {
      return 'group';
    }

    // Chat tab active on: chat screen
    if (['app-chat'].includes(screen)) {
      return 'chat';
    }

    // Profile tab active on: profile, settings, and user-related screens
    if (
      [
        'app-settings',
        'my-profile',
        'profile-data',
        'settings',
        'app-contract-data',
        'app-payroll-info',
      ].includes(screen)
    ) {
      return 'profile';
    }

    return 'dashboard'; // Default
  };

  const activeTab = getActiveTab();

  // Show progress indicator for main flow screens
  const showProgressIndicator = [
    'apply',
    'financial-data',
    'profile-data',
    'dashboard',
    'id-terms',
    'id-capture',
    'id-result',
    'app-contract-data',
    'app-event-application',
    'app-code-of-conduct',
    'app-payroll-info',
  ].includes(currentScreen);

  const currentStepInfo = getCurrentStep();

  return (
    <div className="min-h-screen bg-[var(--ucm-bg-gray)] flex flex-col md:flex-row">
      {/* Desktop Left Sidebar Navigation (≥768px) */}
      {showBottomNav && currentStepInfo.step >= 5 && false && (
        <div className="hidden md:flex fixed left-0 top-0 w-20 h-full bg-white border-r border-[var(--ucm-border-light)] flex-col">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const accessCheck = canAccessScreen(item.screen as any);
            // More lenient access for flexible screens
            const isBlocked =
              !accessCheck.allowed &&
              !['app-event-application', 'app-payroll-info'].includes(
                item.screen
              );

            return (
              <button
                key={item.id}
                onClick={() => navigateWithProcessCheck(item.screen as any)}
                disabled={isBlocked}
                className={`w-20 h-16 flex items-center justify-center transition-colors ${
                  isBlocked
                    ? 'cursor-not-allowed opacity-40'
                    : isActive
                      ? 'bg-[#f3f4f6]'
                      : 'bg-transparent hover:bg-[#f9fafb]'
                }`}
                title={isBlocked ? accessCheck.message : item.label}
              >
                <Icon
                  size={24}
                  className={
                    isBlocked
                      ? 'text-[#d1d5db]'
                      : isActive
                        ? 'text-[var(--ucm-primary-teal)]'
                        : 'text-[#9ca3af]'
                  }
                />
              </button>
            );
          })}
        </div>
      )}

      {/* Main content container with responsive constraints */}
      <div className={`flex-1 flex justify-center`}>
        <div className="w-full max-w-[calc(100vw-32px)] md:max-w-[500px] px-4 py-4">
          {/* Header with back button and optional add button */}
          <div className="flex items-center justify-between mb-4">
            {/* Back button */}
            {showBack && (
              <button
                onClick={handleBack}
                className="w-12 h-12 rounded-full bg-[var(--ucm-disabled-gray)] flex items-center justify-center hover:bg-[var(--ucm-border-light)] transition-colors"
              >
                <ArrowLeft
                  size={20}
                  className="text-[var(--ucm-text-medium)]"
                />
              </button>
            )}

            {/* Add button for appointments */}
            {showAddButton && (
              <button
                onClick={onAddClick}
                className="w-12 h-12 rounded-full bg-[#f3f4f6] flex items-center justify-center hover:bg-[#e5e7eb] transition-colors ml-auto"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-[#6b7280]"
                >
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Main content */}
          {children}
        </div>
      </div>

      {/* Mobile Bottom Navigation (<768px) */}
      {showBottomNav && false && (
        <div className="md:hidden bg-white border-t border-[var(--ucm-border-light)] h-20 flex items-center justify-around px-4">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const accessCheck = canAccessScreen(item.screen as any);
            // More lenient access for flexible screens
            const isBlocked =
              !accessCheck.allowed &&
              !['app-event-application', 'app-payroll-info'].includes(
                item.screen
              );

            return (
              <button
                key={item.id}
                onClick={() => navigateWithProcessCheck(item.screen as any)}
                disabled={isBlocked}
                className={`flex flex-col items-center space-y-1 flex-1 py-2 ${
                  isBlocked ? 'cursor-not-allowed opacity-40' : ''
                }`}
              >
                <Icon
                  size={24}
                  className={
                    isBlocked
                      ? 'text-[#d1d5db]'
                      : isActive
                        ? 'text-[var(--ucm-primary-teal)]'
                        : 'text-[var(--ucm-text-light)]'
                  }
                />
                //{' '}
                <span
                  className={`text-xs font-medium ${
                    isBlocked
                      ? 'text-[#d1d5db]'
                      : isActive
                        ? 'text-[var(--ucm-primary-teal)]'
                        : 'text-[var(--ucm-text-light)]'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
